/**
 * Snapping for the annotation canvas (§3.2 `geo/snap.ts`, §9.2 thresholds).
 *
 * Targets, in priority order: existing vertices (8 px), the horizon line
 * (6 px), edges of existing shapes (6 px), the plate border (6 px), and
 * strong image edges from the Sobel map (6 px). All tolerances arrive in
 * percent already divided by the zoom (`pctTolerance` in `simplify.ts`).
 */
import { closestPointOnSegment } from './polygon'
import { dist, type Bbox, type Pt, type Ring, type Seg } from './types'

export interface SnapTolerances {
  /** Vertex snap radius in percent (spec: 8 px ÷ k). */
  vertex: number
  /** Edge / horizon / border snap radius in percent (spec: 6 px ÷ k). */
  edge: number
}

export interface SnapSources {
  /** Vertices of other shapes (and the current ring's own earlier points). */
  vertices?: Pt[]
  /** Segments of other shapes. */
  edges?: Seg[]
  /** `depth.horizon_y` when set. */
  horizonY?: number
  /** Snap to the 0/100 borders of the plate. */
  frame?: boolean
  /** Edge-map probe: returns the nearest strong image edge within `tol`, or null. */
  imageEdge?: (p: Pt, tol: number) => Pt | null
}

export interface SnapFlags {
  vertices: boolean
  edges: boolean
  image_edges: boolean
  horizon: boolean
  /** Optional grid step in percent. */
  grid?: number
}

export type SnapKind = 'vertex' | 'horizon' | 'edge' | 'frame' | 'image' | 'grid' | 'none'

export interface SnapResult {
  pt: Pt
  kind: SnapKind
}

export const DEFAULT_SNAP_FLAGS: SnapFlags = { vertices: true, edges: true, image_edges: false, horizon: true }

/** Snap `p` against the enabled sources. Returns the input when nothing is within tolerance. */
export function snapPoint(p: Pt, sources: SnapSources, flags: SnapFlags, tol: SnapTolerances): SnapResult {
  if (flags.vertices && sources.vertices?.length) {
    let best: Pt | null = null
    let bestD = tol.vertex
    for (const v of sources.vertices) {
      const d = dist(v, p)
      if (d <= bestD) {
        bestD = d
        best = v
      }
    }
    if (best) return { pt: [best[0], best[1]], kind: 'vertex' }
  }

  let out: Pt = p
  let kind: SnapKind = 'none'

  if (flags.horizon && sources.horizonY !== undefined && Math.abs(p[1] - sources.horizonY) <= tol.edge) {
    out = [out[0], sources.horizonY]
    kind = 'horizon'
  }

  if (flags.edges && sources.edges?.length) {
    let best: Pt | null = null
    let bestD = tol.edge
    for (const [a, b] of sources.edges) {
      const c = closestPointOnSegment(p, a, b)
      const d = dist(c, p)
      if (d <= bestD) {
        bestD = d
        best = c
      }
    }
    if (best) {
      out = kind === 'horizon' ? [best[0], out[1]] : best
      kind = 'edge'
    }
  }

  if (flags.edges && sources.frame !== false) {
    const fx = out[0] <= tol.edge ? 0 : out[0] >= 100 - tol.edge ? 100 : null
    const fy = out[1] <= tol.edge ? 0 : out[1] >= 100 - tol.edge ? 100 : null
    if (fx !== null || fy !== null) {
      out = [fx ?? out[0], fy ?? out[1]]
      kind = 'frame'
    }
  }

  if (kind === 'none' && flags.image_edges && sources.imageEdge) {
    const hit = sources.imageEdge(p, tol.edge)
    if (hit) return { pt: hit, kind: 'image' }
  }

  if (kind === 'none' && flags.grid && flags.grid > 0) {
    const g = flags.grid
    const gx = Math.round(p[0] / g) * g
    const gy = Math.round(p[1] / g) * g
    if (Math.abs(gx - p[0]) <= tol.edge && Math.abs(gy - p[1]) <= tol.edge) return { pt: [gx, gy], kind: 'grid' }
  }

  return { pt: out, kind }
}

/** Collect the vertices and edges of a set of rings for use as snap sources. */
export function snapSourcesFromRings(rings: readonly Ring[]): Pick<SnapSources, 'vertices' | 'edges'> {
  const vertices: Pt[] = []
  const edges: Seg[] = []
  for (const ring of rings) {
    const n = ring.length
    for (let i = 0; i < n; i++) {
      vertices.push(ring[i]!)
      if (n >= 2) edges.push([ring[i]!, ring[(i + 1) % n]!])
    }
  }
  return { vertices, edges }
}

/**
 * Weld vertices closer than `tol` to each other into one, keeping the first.
 * Used after a polygon is closed so a near-duplicate closing click does not
 * leave a zero-length edge.
 */
export function weldRing(ring: Ring, tol: number): Ring {
  const out: Ring = []
  for (const p of ring) {
    if (out.some((q) => dist(p, q) <= tol)) continue
    out.push(p)
  }
  return out
}

/** Snap a whole ring's vertices onto another ring's vertices (the "weld to neighbour" toggle). */
export function weldToRing(ring: Ring, target: Ring, tol: number): Ring {
  return ring.map((p) => {
    let best: Pt = p
    let bestD = tol
    for (const q of target) {
      const d = dist(p, q)
      if (d <= bestD) {
        bestD = d
        best = q
      }
    }
    return best === p ? p : [best[0], best[1]]
  })
}

/** Snap a bbox corner drag to the frame borders. */
export function snapBbox(b: Bbox, tol: number): Bbox {
  const s = (v: number) => (v <= tol ? 0 : v >= 100 - tol ? 100 : v)
  return { x0: s(b.x0), y0: s(b.y0), x1: s(b.x1), y1: s(b.y1) }
}

/** Constrain the segment `from → to` to the nearest 15° (polygon `Shift` lock). */
export function angleLock(from: Pt, to: Pt, stepDeg = 15): Pt {
  const dx = to[0] - from[0]
  const dy = to[1] - from[1]
  const len = Math.hypot(dx, dy)
  if (len === 0) return to
  const step = (stepDeg * Math.PI) / 180
  const ang = Math.round(Math.atan2(dy, dx) / step) * step
  return [from[0] + Math.cos(ang) * len, from[1] + Math.sin(ang) * len]
}
